import React from 'react';
import { ArrowLeftRight, ShieldCheck, Rocket, ExternalLink, Info, Wallet } from 'lucide-react';

// ── Swap page ───────────────────────────────────────────────────────
//
// Cross-chain swaps powered by THORChain, integrated so BCHNostr earns the
// affiliate fee (THORName "Bch", 10 bps) on every swap.
//
// SECURITY + WALLETS: this app stores the user's Nostr nsec and BCH wallet WIF
// in plaintext localStorage. Earning the affiliate fee requires running
// THORChain's `widget.js` with our API key, which must never run inside the
// app itself. We previously embedded the widget in a cross-origin iframe, but
// wallet extensions / WalletConnect generally refuse to inject into or drive
// an iframe — connecting a wallet failed.
//
// So the swap widget is launched as a TOP-LEVEL page (`/thorchain-widget.html`)
// instead of being embedded. Top-level is exactly what wallet dApps expect, so
// wallet connect works. The widget page still carries our affiliate key (we
// earn) and never loads the app's React/key code — and because launching it is
// a full top-level navigation, the app (and its in-memory key handling) is torn
// down first. Same-origin is fine here: the risk we were guarding against was
// an EMBEDDED third-party script reading storage live; a standalone top-level
// page that contains none of our app code has no such access path.

const WIDGET_PATH = '/thorchain-widget.html';
// THORChain's hosted interface — an alternative destination if the user prefers.
const SWAP_ORIGIN = 'https://swap.thorchain.org';

// Prefill the pair from the user's original request (BTC → ETH).
const DEFAULT_SELL = 'BTC.BTC';
const DEFAULT_BUY = 'ETH.ETH';

function buildWidgetUrl(sell: string, buy: string): string {
  const u = new URL(WIDGET_PATH, window.location.origin);
  u.searchParams.set('sellAsset', sell);
  u.searchParams.set('buyAsset', buy);
  return u.toString();
}

function buildExternalUrl(sell: string, buy: string): string {
  const u = new URL(SWAP_ORIGIN);
  u.searchParams.set('sellAsset', sell);
  u.searchParams.set('buyAsset', buy);
  return u.toString();
}

export default function SwapPage() {
  const swapUrl = buildWidgetUrl(DEFAULT_SELL, DEFAULT_BUY);
  const externalUrl = buildExternalUrl(DEFAULT_SELL, DEFAULT_BUY);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-primary" /> Swap
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Cross-chain swaps across BTC, ETH, and more — powered by THORChain.
        </p>
      </div>

      <div className="p-4 max-w-md mx-auto space-y-4">
        {/* Hero card */}
        <div className="glass-card border-primary/20 p-6 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <ArrowLeftRight className="w-7 h-7 text-primary" />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-bold">Swap crypto, cross-chain</h2>
            <p className="text-sm text-muted-foreground">
              Trade BTC, ETH, and dozens of assets natively across chains. You
              connect your own wallet and sign every swap yourself.
            </p>
          </div>

          <a
            href={swapUrl}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors"
          >
            <Rocket className="w-4 h-4" /> Launch Swap
          </a>

          <a
            href={swapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in a new tab instead
          </a>
        </div>

        {/* Why it opens as its own page */}
        <div className="glass-card p-3 flex items-start gap-2.5">
          <Wallet className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            The swap opens as its own page — wallet apps and browser extensions
            need a full page to connect, so this is where you'll link your wallet
            and confirm the trade.
          </p>
        </div>

        {/* Security note */}
        <div className="glass-card p-3 flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            The swap runs isolated from BCHNostr. It can't touch your Nostr keys
            or in-app BCH wallet — you swap entirely with{' '}
            <span className="font-semibold text-foreground">your own connected wallet</span>.
          </p>
        </div>

        {/* Disclosure */}
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <p>
            THORChain is an independent, decentralized protocol; swaps are
            provided as-is and are not operated by BCHNostr. Prefer THORChain's
            own interface? Use{' '}
            <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
              swap.thorchain.org
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
